import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import {
    RefreshCw,
    Play,
    Eye,
    AlertCircle,
    AlertTriangle,
    ShieldAlert,
    ShieldCheck,
    Database,
    KeyRound,
    Wand2,
} from "lucide-react";

import DataTable from "@/components/shared/DataTable";
import TableSkeleton from "@/components/shared/TableSkeleton";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Callout } from "@/components/ui/Callout";
import AlertModal from "@/components/shared/AlertModal";
import Skeleton from "@/components/shared/Skeleton";
import CustomTooltip from "@/components/shared/CustomTooltip";
import SearchInput from "@/components/shared/SearchInput";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { plural } from "@/utils/formatText";

import { PILL, PILL_NEUTRAL, PILL_PRIMARY, SOLID_WARNING, TONE } from "./constants";
import { useVersionCatalog } from "./hooks/useVersionCatalog";
import RlsApplyModal from "./components/RlsApplyModal";
import PermissionsSyncModal from "./components/PermissionsSyncModal";
import AlembicApplyModal from "./components/AlembicApplyModal";
import AlembicDowngradeModal from "./components/AlembicDowngradeModal";
import MigrationHistoryModal from "./components/MigrationHistoryModal";
import RlsFleetRollbackModal from "./components/RlsFleetRollbackModal";
import DangerMenu from "./components/DangerMenu";
import EmptyState from "./components/EmptyState";
import FleetTile from "./components/FleetTile";
import HealthBadge from "./components/HealthBadge";
import PermissionsCell from "./components/PermissionsCell";
import PermissionsDetailDrawer from "./components/PermissionsDetailDrawer";
import RlsVersionDrawer from "./components/RlsVersionDrawer";
import RollbackHazard from "./components/RollbackHazard";
import RolloutStep from "./components/RolloutStep";
import RowActionsMenu from "./components/RowActionsMenu";
import SchemaRevisionDrawer from "./components/SchemaRevisionDrawer";
import StateChip from "./components/StateChip";
import TagChip from "./components/TagChip";

// SuperAdmin Database Management — the fleet's drift dashboard.
//
// The screen answers one question three times: is every database carrying the
// code's schema (Alembic), its row-security policies (RLS), and its permission
// catalog? Each answer gets a tile, a table column and one fleet-wide action,
// in the same order, so a count on a tile always names a column beside it.
//
// This file holds the fleet arithmetic and the actions; every piece it draws
// with lives in `./components`, and the tones and pill shapes they share live in
// `./constants`.
//
// Colour never carries a state on its own — every chip pairs its tone with a
// word and an icon, and the tones come from `--tone-*` so they hold in both
// themes.
const DatabaseManagement = () => {
    const setHeader = useOutletContext();

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [checkedAt, setCheckedAt] = useState(null);
    const [rlsCodeVersion, setRlsCodeVersion] = useState(null);
    const [alembicHead, setAlembicHead] = useState(null);
    // Canary rollout state
    const [canaryTenantSlug, setCanaryTenantSlug] = useState("");
    const [canaryTenantId, setCanaryTenantId] = useState(null);
    const [canaryInFlight, setCanaryInFlight] = useState(false);
    const [remainingInFlight, setRemainingInFlight] = useState(false);

    // Permissions status (per-dedicated-DB drift map). `permStatusFailed`
    // separates "no drift" from "we could not ask" — the two read the same
    // otherwise, and only one of them is good news.
    const [permStatusByKey, setPermStatusByKey] = useState({});
    const [permTotal, setPermTotal] = useState(null);
    const [permStatusFailed, setPermStatusFailed] = useState(false);

    // Modals — one target at a time per action kind
    const [applyTarget, setApplyTarget] = useState(null); // RLS
    const [previewMode, setPreviewMode] = useState(false);
    const [permApplyTarget, setPermApplyTarget] = useState(null);
    const [alembicTarget, setAlembicTarget] = useState(null);

    // Whether the last load asked for the row-security coverage scan. Without
    // it, "no stranded tables" and "we never looked" would render the same, and
    // only one of those is good news.
    const [coverageChecked, setCoverageChecked] = useState(false);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [busyKey, setBusyKey] = useState(null);

    // Fleet reconcile confirm modal
    const [reconcileConfirmOpen, setReconcileConfirmOpen] = useState(false);
    const [reconcileInFlight, setReconcileInFlight] = useState(false);

    // Alembic downgrade confirm — target row or the synthetic 'all' row.
    const [downgradeTarget, setDowngradeTarget] = useState(null);
    const [downgradeInFlight, setDowngradeInFlight] = useState(false);

    // Per-tenant migration history modal.
    const [historyRow, setHistoryRow] = useState(null);

    // Per-tenant RLS rollback confirm.
    const [rlsRollbackTarget, setRlsRollbackTarget] = useState(null);
    const [rlsRollbackInFlight, setRlsRollbackInFlight] = useState(false);

    // Fleet-wide RLS rollback (modal owns its own version + confirm gate).
    const [fleetRollbackOpen, setFleetRollbackOpen] = useState(false);

    // Read-only detail drawers — one row at a time each. They say what a
    // version number means; nothing here changes a database.
    const [rlsDetailRow, setRlsDetailRow] = useState(null);
    const [schemaDetailRow, setSchemaDetailRow] = useState(null);
    const [permDetailRow, setPermDetailRow] = useState(null);

    // What the numbers mean: the RLS version archive and the revision graph,
    // both read from code and fetched once. Shared by the chips, the drawers and
    // both rollback confirmations, so they cannot describe a version
    // differently. A failure here leaves the table working on bare numbers.
    const catalog = useVersionCatalog();

    // `includeUnpoliced` asks the server to also scan each database for tables
    // that have row security on and no policy at all. It costs a query per
    // database, so the ordinary load and every post-action refresh leave it
    // off; the coverage check in the toolbar is what turns it on.
    const load = useCallback(async ({ includeUnpoliced = false } = {}) => {
        try {
            setLoading(true);
            setLoadError(null);
            setCoverageChecked(includeUnpoliced);
            const [rlsData, permData] = await Promise.all([
                PlatformService.fetchRlsStatus({ includeUnpoliced }),
                PlatformService.fetchPermissionsStatus().catch((e) => {
                    console.warn("Permissions status fetch failed:", e);
                    return null;
                }),
            ]);
            // Wire is camelCase (AppBaseCamelCaseModel).
            setRows(rlsData?.databases ?? []);
            setRlsCodeVersion(rlsData?.rlsCodeVersion ?? null);
            setAlembicHead(rlsData?.alembicHead ?? null);
            setCanaryTenantSlug(rlsData?.canaryTenantSlug ?? "");
            setCanaryTenantId(rlsData?.canaryTenantId ?? null);

            const map = {};
            for (const p of permData?.databases ?? []) map[p.key] = p;
            setPermStatusByKey(map);
            setPermTotal(permData?.platformTotal ?? null);
            setPermStatusFailed(permData === null);
            setCheckedAt(new Date());
        } catch (err) {
            const message = apiErrorMessageFormat(err);
            setRows([]);
            setLoadError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // ── Fleet arithmetic ────────────────────────────────────────────────
    // A database that could not be probed is neither behind nor up to date.
    // It gets its own count, because "we do not know" is the state that needs
    // a human, and folding it into either of the other two hides it.
    const platformRow = useMemo(
        () => rows.find((r) => r.isPlatform) ?? null,
        [rows]
    );
    const dedicatedRows = useMemo(
        () => rows.filter((r) => !r.isPlatform),
        [rows]
    );
    const unreachableRows = useMemo(() => rows.filter((r) => r.error), [rows]);

    // Databases carrying a table with row security on and no policy at all —
    // deny-everything for the app role, and silent: reads return nothing rather
    // than failing. Empty unless the coverage scan has been run.
    const strandedRows = useMemo(
        () => rows.filter((r) => r.unpolicedTables?.length),
        [rows]
    );

    // Alembic: /alembic/apply skips the platform DB by design, so the tile
    // counts dedicated DBs only. The platform's own drift gets a callout.
    const alembicBehind = useMemo(
        () => dedicatedRows.filter((r) => !r.error && r.alembicDrifted),
        [dedicatedRows]
    );
    // RLS: /rls/apply with target 'all' does include the platform DB.
    const rlsBehind = useMemo(
        () => rows.filter((r) => !r.error && r.rlsDrifted),
        [rows]
    );
    const permBehind = useMemo(
        () =>
            dedicatedRows.filter((r) => {
                const p = permStatusByKey[r.key];
                return p && !p.error && !p.inSync;
            }),
        [dedicatedRows, permStatusByKey]
    );
    const permUnknown = useMemo(
        () =>
            dedicatedRows.filter((r) => {
                const p = permStatusByKey[r.key];
                return !p || !!p.error;
            }),
        [dedicatedRows, permStatusByKey]
    );

    const attentionCount =
        alembicBehind.length +
        rlsBehind.length +
        permBehind.length +
        unreachableRows.length;

    // ── What the code's own versions are called ─────────────────────────
    // The tiles report the version every database is being measured against, so
    // they read the catalogue for the *code's* version rather than any one
    // database's.
    const headRevision = catalog.revision(alembicHead);
    const codeRlsEntry = catalog.rlsVersion(rlsCodeVersion);

    // How many permission codes are out of step across the fleet, not how many
    // databases are. "1 behind" is one database and an unknown number of codes;
    // this says whether that is one code or two hundred.
    const permDriftNote = useMemo(() => {
        const totals = permBehind.reduce(
            (acc, r) => {
                const p = permStatusByKey[r.key];
                return {
                    added: acc.added + (p?.addedCount ?? 0),
                    updated: acc.updated + (p?.updatedCount ?? 0),
                    removed: acc.removed + (p?.removedCount ?? 0),
                };
            },
            { added: 0, updated: 0, removed: 0 }
        );
        const parts = [];
        if (totals.added) parts.push(`${totals.added} to add`);
        if (totals.updated) parts.push(`${totals.updated} to update`);
        if (totals.removed) parts.push(`${totals.removed} to remove`);
        if (!parts.length) return undefined;
        return `${parts.join(", ")} across ${plural(
            permBehind.length,
            "database",
            "databases"
        )}.`;
    }, [permBehind, permStatusByKey]);

    // Reconcile runs Alembic + RLS on dedicated DBs only — the platform DB is
    // never a target, so platform drift must not enable the button.
    const reconcileNeeded =
        alembicBehind.length > 0 ||
        rlsBehind.some((r) => !r.isPlatform);

    const anyActionInFlight =
        canaryInFlight ||
        remainingInFlight ||
        reconcileInFlight ||
        downgradeInFlight ||
        rlsRollbackInFlight;
    const controlsBusy = loading || anyActionInFlight;

    // ── Canary rollout state, read from the fleet rather than from memory ──
    // The old flag lived in component state, so a refresh lost the fact that
    // step 1 had run. The canary's own RLS version answers it and survives a
    // reload.
    const canaryRow = useMemo(
        () =>
            canaryTenantId
                ? rows.find((r) => r.tenantId === canaryTenantId) ?? null
                : null,
        [rows, canaryTenantId]
    );
    const canaryReachable = !!canaryRow && !canaryRow.error;
    const canaryBehind = canaryReachable && canaryRow.rlsDrifted;
    const canaryCurrent = canaryReachable && !canaryRow.rlsDrifted;
    // "Remaining" is every database that is not the canary, the platform DB
    // included — /rls/apply-remaining covers it, because the shared tier can
    // otherwise be left on the old policies with nothing to move it.
    const remainingBehind = useMemo(
        () =>
            rows.filter(
                (r) => r.tenantId !== canaryTenantId && !r.error && r.rlsDrifted
            ),
        [rows, canaryTenantId]
    );

    useEffect(() => {
        setHeader({
            title: "Database Management",
            subTitle: loading ? (
                <Skeleton className="h-4 w-48" />
            ) : (
                `${plural(rows.length, "database", "databases")} · ${
                    attentionCount === 0
                        ? "all up to date"
                        : `${plural(attentionCount, "update", "updates")} pending`
                }`
            ),
        });
        return () => setHeader({ title: "", subTitle: "" });
    }, [setHeader, loading, rows.length, attentionCount]);

    // ── Search ─────────────────────────────────────────────────────────
    // Client-side filter on label/host/dbName/slug — the fleet is small enough
    // that the whole snapshot is already on the page.
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            [r.label, r.host, r.dbName, r.tenantSlug]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        );
    }, [rows, search]);

    // A filter that moved the last page out from under the reader leaves them
    // staring at an empty page they cannot page back from.
    useEffect(() => {
        setPage(1);
    }, [search, pageSize, filteredRows.length]);

    // Each row carries the three states it is sorted by. DataTable sorts the
    // raw field, so a state has to be on the row, not only inside the cell.
    //
    // The two version states come from the server's `*Position`, which
    // distinguishes 'ahead' and 'diverged' from 'behind' — differences from the
    // code that pressing Apply does not fix. The `*Drifted` fallback is what
    // this screen used before those fields existed and still answers correctly
    // for a server that does not send them; the tiles and every action gate
    // read `*Drifted` throughout, so what counts as "behind the code" for a
    // button has not moved.
    const tableRows = useMemo(
        () =>
            filteredRows.map((r) => {
                const perm = permStatusByKey[r.key];
                return {
                    ...r,
                    schemaState: r.error
                        ? "unknown"
                        : r.alembicPosition ??
                          (r.alembicDrifted ? "behind" : "current"),
                    rlsState: r.error
                        ? "unknown"
                        : r.rlsPosition ?? (r.rlsDrifted ? "behind" : "current"),
                    permState: r.isPlatform
                        ? "source"
                        : !perm || perm.error
                        ? "unknown"
                        : perm.inSync
                        ? "current"
                        : "behind",
                };
            }),
        [filteredRows, permStatusByKey]
    );

    // ── Actions ────────────────────────────────────────────────────────
    const onPreview = (row) => {
        setApplyTarget(row);
        setPreviewMode(true);
    };
    const onApplyRls = (row) => {
        setApplyTarget(row);
        setPreviewMode(false);
    };
    const onApplyRlsAll = () => {
        setApplyTarget({
            key: "all",
            label: "All databases (platform + dedicated)",
            isSyntheticAll: true,
        });
        setPreviewMode(false);
    };

    const onReload = async (row) => {
        if (!row?.tenantId) return;
        try {
            setBusyKey(row.key);
            await PlatformService.reloadEngine(row.tenantId);
            toast.success(`Engine reloaded for ${row.tenantSlug}.`);
            await load();
        } catch (err) {
            toast.error(apiErrorMessageFormat(err));
        } finally {
            setBusyKey(null);
        }
    };

    const onPermApply = (row) => {
        if (!row?.tenantId) return;
        setPermApplyTarget(row);
    };
    const onPermApplyAll = () => {
        setPermApplyTarget({
            key: "all",
            label: "All dedicated databases (permissions)",
            isSyntheticAll: true,
        });
    };

    const onAlembicApply = (row) => {
        if (!row?.tenantId) return;
        setAlembicTarget(row);
    };
    const onAlembicApplyAll = () => {
        setAlembicTarget({
            key: "all",
            label: "All dedicated databases (Alembic upgrade head)",
            isSyntheticAll: true,
        });
    };

    // Alembic downgrade — fleet-wide or a single row, always one revision back.
    const onDowngradeConfirm = useCallback(async () => {
        if (!downgradeTarget) return;
        try {
            setDowngradeInFlight(true);
            const target =
                downgradeTarget.key === "all"
                    ? "all"
                    : `tenant:${downgradeTarget.tenantId}`;
            const res = await PlatformService.downgradeAlembic({
                target,
                targetRevision: "-1",
            });
            const results = res?.results || [];
            const failed = results.filter((r) => !r.success).length;
            if (failed === 0) {
                toast.success(
                    `Rolled back one Alembic revision on ${plural(
                        results.length,
                        "database",
                        "databases"
                    )}.`
                );
            } else {
                toast.error(
                    `${plural(failed, "database", "databases")} failed to downgrade. Open Migration history for the reason.`
                );
            }
            setDowngradeTarget(null);
            await load();
        } catch (err) {
            toast.error(apiErrorMessageFormat(err));
        } finally {
            setDowngradeInFlight(false);
        }
    }, [downgradeTarget, load]);

    // Roll back one RLS version on a single DB (current - 1).
    const onRlsRollbackConfirm = useCallback(async () => {
        if (!rlsRollbackTarget) return;
        const currentVersion = rlsRollbackTarget.rlsCurrent;
        if (!currentVersion || currentVersion <= 1) {
            toast.error("No earlier RLS version to roll back to.");
            setRlsRollbackTarget(null);
            return;
        }
        try {
            setRlsRollbackInFlight(true);
            const target = currentVersion - 1;
            const res = await PlatformService.applyRlsVersionToTenant(
                rlsRollbackTarget.tenantId,
                target
            );
            const ok = (res?.results || []).every((r) => r.success);
            if (ok) {
                toast.success(
                    `Rolled back ${rlsRollbackTarget.tenantSlug || "database"} to RLS v${target}.`
                );
            } else {
                toast.error(
                    "Rollback failed. Open Migration history on this row for the reason."
                );
            }
            setRlsRollbackTarget(null);
            await load();
        } catch (err) {
            toast.error(apiErrorMessageFormat(err));
        } finally {
            setRlsRollbackInFlight(false);
        }
    }, [rlsRollbackTarget, load]);

    // Canary rollout step 1 — apply to the canary; step 2 unlocks once the
    // canary reports the current version back.
    const onApplyCanary = useCallback(async () => {
        if (!canaryTenantSlug) return;
        try {
            setCanaryInFlight(true);
            const res = await PlatformService.applyRlsCanary();
            const ok = (res?.results || []).every((r) => r.success);
            if (ok) {
                toast.success(
                    `Canary (${canaryTenantSlug}) is on v${rlsCodeVersion}. Verify it, then roll out to the rest.`
                );
            } else {
                toast.error(
                    "Canary apply failed. Open Migration history on that row for the reason."
                );
            }
            await load();
        } catch (err) {
            toast.error(apiErrorMessageFormat(err));
        } finally {
            setCanaryInFlight(false);
        }
    }, [canaryTenantSlug, rlsCodeVersion, load]);

    // Canary rollout step 2 — apply to every dedicated DB except the canary.
    const onApplyRemaining = useCallback(async () => {
        try {
            setRemainingInFlight(true);
            const res = await PlatformService.applyRlsRemaining();
            const results = res?.results || [];
            const failed = results.filter((r) => !r.success).length;
            if (results.length === 0) {
                toast.success("No remaining databases needed an apply.");
            } else if (failed === 0) {
                toast.success(
                    `Applied RLS v${rlsCodeVersion} to ${plural(
                        results.length,
                        "database",
                        "databases"
                    )}.`
                );
            } else {
                toast.error(
                    `${plural(failed, "database", "databases")} failed. Open Migration history for the reason.`
                );
            }
            await load();
        } catch (err) {
            toast.error(apiErrorMessageFormat(err));
        } finally {
            setRemainingInFlight(false);
        }
    }, [rlsCodeVersion, load]);

    // Fleet reconcile — Alembic upgrade + RLS apply per dedicated DB.
    const onReconcileConfirm = useCallback(async () => {
        try {
            setReconcileInFlight(true);
            const res = await PlatformService.reconcileFleet();
            const reconciled = res?.reconciledCount ?? 0;
            const failed = res?.failedCount ?? 0;
            if (failed === 0) {
                toast.success(
                    `Fleet reconciled — ${plural(reconciled, "database is", "databases are")} up to date.`
                );
            } else {
                toast.error(
                    `Reconcile finished: ${reconciled} succeeded, ${failed} failed. Check the rows below.`
                );
            }
            setReconcileConfirmOpen(false);
            await load();
        } catch (err) {
            toast.error(apiErrorMessageFormat(err));
        } finally {
            setReconcileInFlight(false);
        }
    }, [load]);

    const COLUMNS = useMemo(
        () => [
            {
                key: "label",
                label: "Database",
                sortable: true,
                align: "left",
                render: (val, row) => {
                    const isCanary =
                        canaryTenantId && row.tenantId === canaryTenantId;
                    return (
                        <div className="min-w-0 max-w-[320px] py-1">
                            <div className="flex items-center gap-1.5 min-w-0">
                                <span
                                    className="block overflow-hidden text-ellipsis whitespace-nowrap text-left font-medium text-[13px] text-text-primary"
                                    title={row.dbName ? `${val} (${row.dbName})` : val}
                                >
                                    {val || "—"}
                                </span>
                                {row.isPlatform && (
                                    <TagChip
                                        tone={TONE.slate}
                                        label="platform"
                                        title="The shared database. Alembic upgrades on this one run from the server shell, not from this screen."
                                    />
                                )}
                                {isCanary && (
                                    <TagChip
                                        tone={TONE.violet}
                                        label="canary"
                                        title="Canary tenant — RLS lands here first for verification, before the rest of the fleet."
                                    />
                                )}
                            </div>
                            {row.host && (
                                <div
                                    className="text-[11px] text-text-secondary mt-0.5 font-mono truncate"
                                    title={row.host}
                                >
                                    {row.host}
                                </div>
                            )}
                            {row.error && (
                                <div
                                    className="mt-1 flex items-center gap-1 text-[11px] text-tone-rose"
                                    title={row.error}
                                >
                                    <AlertCircle size={11} className="shrink-0" />
                                    <span className="truncate">
                                        Could not read this database
                                    </span>
                                </div>
                            )}
                            {row.unpolicedTables?.length > 0 && (
                                <div
                                    className="mt-1 flex items-center gap-1 text-[11px] text-tone-amber"
                                    title={`Row security is on with no policy at all on: ${row.unpolicedTables.join(
                                        ", "
                                    )}`}
                                >
                                    <ShieldAlert size={11} className="shrink-0" />
                                    <span className="truncate">
                                        {plural(
                                            row.unpolicedTables.length,
                                            "table locked to nobody",
                                            "tables locked to nobody"
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                key: "schemaState",
                label: "Schema",
                sortable: true,
                align: "center",
                render: (state, row) => (
                    <div className="flex justify-center">
                        <StateChip
                            state={state}
                            label={
                                row.alembicCurrent
                                    ? row.alembicCurrent.slice(0, 7)
                                    : "—"
                            }
                            mono
                            title={schemaTitle(row, catalog)}
                            onClick={
                                row.error ? undefined : () => setSchemaDetailRow(row)
                            }
                        />
                    </div>
                ),
            },
            {
                key: "rlsState",
                label: "Row security",
                sortable: true,
                align: "center",
                render: (state, row) => (
                    <div className="flex justify-center">
                        <StateChip
                            state={state}
                            label={row.rlsCurrent == null ? "—" : `v${row.rlsCurrent}`}
                            mono
                            title={rlsTitle(row, catalog)}
                            onClick={
                                row.error ? undefined : () => setRlsDetailRow(row)
                            }
                        />
                    </div>
                ),
            },
            {
                key: "permState",
                label: "Permissions",
                sortable: true,
                align: "center",
                render: (state, row) => (
                    <div className="flex justify-center">
                        <PermissionsCell
                            state={state}
                            status={permStatusByKey[row.key]}
                            platformTotal={permTotal}
                            // Only where there is a diff to name: the platform
                            // row is the source, and an unreadable one has
                            // nothing to fetch.
                            onClick={
                                state === "source" || state === "unknown"
                                    ? undefined
                                    : () => setPermDetailRow(row)
                            }
                        />
                    </div>
                ),
            },
            {
                key: "health",
                label: "Health",
                sortable: true,
                align: "center",
                render: (val) => (
                    <div className="flex justify-center">
                        <HealthBadge health={val} />
                    </div>
                ),
            },
            {
                key: "actions",
                label: "Actions",
                sortable: false,
                align: "center",
                render: (_val, row) => (
                    <div className="flex gap-2 justify-center">
                        <CustomTooltip
                            text={
                                row.error
                                    ? "Unavailable — this database could not be read"
                                    : "Preview the RLS statements"
                            }
                        >
                            <Button
                                onClick={() => onPreview(row)}
                                disabled={!!row.error}
                                aria-label={`Preview RLS for ${row.label}`}
                                className="px-3 py-2 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Eye size={14} aria-hidden="true" />
                            </Button>
                        </CustomTooltip>
                        <CustomTooltip
                            text={
                                row.error
                                    ? "Unavailable — this database could not be read"
                                    : "Apply RLS to this database"
                            }
                        >
                            <Button
                                onClick={() => onApplyRls(row)}
                                disabled={!!row.error}
                                aria-label={`Apply RLS to ${row.label}`}
                                className="px-3 py-2 rounded-xl bg-button-secondary text-text-base text-sm hover:bg-button-secondary/85 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Play size={14} aria-hidden="true" />
                            </Button>
                        </CustomTooltip>
                        {row.tenantId && (
                            <RowActionsMenu
                                row={row}
                                busyKey={busyKey}
                                catalog={catalog}
                                onPermApply={onPermApply}
                                onAlembicApply={onAlembicApply}
                                onAlembicDowngrade={(r) => setDowngradeTarget(r)}
                                onRlsRollback={(r) => setRlsRollbackTarget(r)}
                                onHistory={(r) => setHistoryRow(r)}
                                onReload={onReload}
                            />
                        )}
                    </div>
                ),
            },
        ],
        [permStatusByKey, permTotal, busyKey, canaryTenantId, catalog]
    );

    return (
        <PermissionGuard permission={PERMISSIONS.SUPER_ADMIN_MANAGE}>
            <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>
                {/* Toolbar — what the snapshot is, and the two whole-fleet moves */}
                <Card className="p-3 border-border-primary">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[13px] font-semibold text-text-primary">
                                Fleet status
                            </div>
                            <div className="text-[11px] text-text-secondary mt-0.5">
                                {loading ? (
                                    "Checking every database…"
                                ) : loadError ? (
                                    <span className="text-tone-rose">
                                        Could not read the fleet.
                                    </span>
                                ) : attentionCount === 0 ? (
                                    <span className="text-tone-emerald">
                                        Every database matches the code.
                                    </span>
                                ) : (
                                    <span className="text-tone-amber">
                                        {plural(attentionCount, "update", "updates")}{" "}
                                        pending across{" "}
                                        {plural(rows.length, "database", "databases")}.
                                    </span>
                                )}
                                {checkedAt && !loading && (
                                    <span className="text-text-muted">
                                        {" "}
                                        Checked at{" "}
                                        {checkedAt.toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                        .
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                onClick={() => load()}
                                disabled={controlsBusy}
                                className={PILL_NEUTRAL}
                            >
                                <RefreshCw
                                    size={12}
                                    aria-hidden="true"
                                    className={loading ? "animate-spin" : ""}
                                />
                                {loading ? "Checking…" : "Refresh"}
                            </Button>
                            <CustomTooltip
                                text={
                                    "Scan every database for tables that have row " +
                                    "security on and no policy — locked to nobody, " +
                                    "and silent about it. One extra query per database."
                                }
                            >
                                <Button
                                    onClick={() => load({ includeUnpoliced: true })}
                                    disabled={controlsBusy}
                                    className={PILL_NEUTRAL}
                                >
                                    <ShieldAlert size={12} aria-hidden="true" />
                                    Check coverage
                                </Button>
                            </CustomTooltip>
                            <CustomTooltip
                                text={
                                    reconcileNeeded
                                        ? "Alembic upgrade, then RLS apply, on every dedicated database"
                                        : "Nothing to reconcile — every dedicated database is current"
                                }
                            >
                                <Button
                                    onClick={() => setReconcileConfirmOpen(true)}
                                    disabled={controlsBusy || !reconcileNeeded}
                                    className={PILL_PRIMARY}
                                >
                                    <Wand2 size={12} aria-hidden="true" />
                                    {reconcileInFlight
                                        ? "Reconciling…"
                                        : "Reconcile fleet"}
                                </Button>
                            </CustomTooltip>
                            <DangerMenu
                                disabled={controlsBusy}
                                onFleetRollback={() => setFleetRollbackOpen(true)}
                                onFleetDowngrade={() =>
                                    setDowngradeTarget({
                                        key: "all",
                                        label: "All dedicated databases",
                                        isSyntheticAll: true,
                                    })
                                }
                            />
                        </div>
                    </div>
                </Card>

                {/* One tile per drift axis, in the order of the table columns */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    <FleetTile
                        icon={Database}
                        title="Schema"
                        target={alembicHead ? alembicHead.slice(0, 7) : "…"}
                        targetLabel="Code head"
                        targetName={headRevision?.title}
                        targetTitle={
                            alembicHead
                                ? [
                                      `Code head: ${alembicHead}`,
                                      headRevision?.title,
                                      headRevision?.createdAt &&
                                          `added ${headRevision.createdAt}`,
                                  ]
                                      .filter(Boolean)
                                      .join(" · ")
                                : undefined
                        }
                        loading={loading}
                        total={dedicatedRows.length}
                        behind={alembicBehind.length}
                        unknown={unreachableRows.filter((r) => !r.isPlatform).length}
                        unit="dedicated database"
                        actionLabel={
                            alembicBehind.length === 0
                                ? "Nothing to upgrade"
                                : `Upgrade ${plural(
                                      alembicBehind.length,
                                      "database",
                                      "databases"
                                  )}`
                        }
                        actionIcon={Play}
                        actionTitle="Run alembic upgrade head on every dedicated database that is behind"
                        disabledTitle="Every dedicated database is on the code head"
                        onAction={onAlembicApplyAll}
                        disabled={controlsBusy || alembicBehind.length === 0}
                    />
                    <FleetTile
                        icon={ShieldCheck}
                        title="Row security"
                        target={rlsCodeVersion == null ? "…" : `v${rlsCodeVersion}`}
                        targetLabel="Code version"
                        targetName={codeRlsEntry?.title}
                        // The summary is a paragraph, so it belongs on hover
                        // rather than in the tile — but it is the answer to
                        // "what is v16", and this is where that gets asked.
                        targetTitle={
                            rlsCodeVersion == null
                                ? undefined
                                : [
                                      `Code version: v${rlsCodeVersion}`,
                                      codeRlsEntry?.title,
                                      codeRlsEntry?.summary,
                                  ]
                                      .filter(Boolean)
                                      .join(" · ")
                        }
                        note={
                            codeRlsEntry
                                ? `Polices ${plural(
                                      codeRlsEntry.totalTablesPoliced,
                                      "table",
                                      "tables"
                                  )} in ${plural(
                                      codeRlsEntry.statementCount,
                                      "statement",
                                      "statements"
                                  )}.`
                                : undefined
                        }
                        loading={loading}
                        total={rows.length}
                        behind={rlsBehind.length}
                        unknown={unreachableRows.length}
                        unit="database"
                        actionLabel={
                            canaryTenantSlug
                                ? "Staged rollout below"
                                : rlsBehind.length === 0
                                ? "Nothing to apply"
                                : `Apply to ${plural(
                                      rlsBehind.length,
                                      "database",
                                      "databases"
                                  )}`
                        }
                        actionIcon={Play}
                        actionTitle="Apply the current RLS policies to every database that is behind"
                        disabledTitle={
                            canaryTenantSlug
                                ? "A canary is configured — use the staged rollout below"
                                : "Every database is on the current RLS version"
                        }
                        onAction={onApplyRlsAll}
                        disabled={
                            controlsBusy ||
                            !!canaryTenantSlug ||
                            rlsBehind.length === 0
                        }
                    />
                    <FleetTile
                        icon={KeyRound}
                        title="Permissions"
                        target={permTotal == null ? "…" : String(permTotal)}
                        targetLabel="Codes in the catalog"
                        // There is no version here — the platform's own table is
                        // the source — so the name slot says where the number
                        // comes from instead.
                        targetName="held on the platform database"
                        targetTitle={
                            permTotal == null
                                ? undefined
                                : `${plural(
                                      permTotal,
                                      "permission code",
                                      "permission codes"
                                  )} on the platform database. Every dedicated database is synced from this list.`
                        }
                        note={permDriftNote}
                        loading={loading}
                        total={dedicatedRows.length}
                        behind={permBehind.length}
                        unknown={permUnknown.length}
                        unit="dedicated database"
                        actionLabel={
                            permBehind.length === 0
                                ? "Nothing to sync"
                                : `Sync ${plural(
                                      permBehind.length,
                                      "database",
                                      "databases"
                                  )}`
                        }
                        actionIcon={ShieldCheck}
                        actionTitle="Copy the platform permission catalog into every dedicated database that has drifted"
                        disabledTitle="Every dedicated database carries the platform catalog"
                        onAction={onPermApplyAll}
                        disabled={controlsBusy || permBehind.length === 0}
                    />
                </div>

                {/* The two things this screen cannot fix itself */}
                {!loading && platformRow?.alembicDrifted && (
                    <Callout tone="warning" icon={AlertTriangle} size="xs">
                        The platform database is on{" "}
                        <span className="font-mono">
                            {platformRow.alembicCurrent
                                ? platformRow.alembicCurrent.slice(0, 7)
                                : "no revision"}
                        </span>{" "}
                        and the code head is{" "}
                        <span className="font-mono">
                            {alembicHead ? alembicHead.slice(0, 7) : "unknown"}
                        </span>
                        . No button here upgrades it — run{" "}
                        <span className="font-mono">alembic upgrade head</span> on the
                        server first, then reconcile the fleet.
                    </Callout>
                )}
                {!loading && permStatusFailed && (
                    <Callout tone="warning" icon={AlertTriangle} size="xs">
                        Permission drift could not be checked — the Permissions column
                        reads "unknown" for every database. Refresh to try again.
                    </Callout>
                )}

                {/* The coverage scan's answer. A table with row security on and
                    no policy is locked to nobody and says nothing about it —
                    reads return zero rows rather than failing — so it is found
                    by looking, never by an error. */}
                {!loading && coverageChecked && strandedRows.length > 0 && (
                    <Callout tone="warning" icon={ShieldAlert} size="xs">
                        <span className="font-medium block mb-0.5">
                            {plural(strandedRows.length, "database has", "databases have")}{" "}
                            a table with row security on and no policy
                        </span>
                        Nothing can read or write those tables, and a read returns
                        nothing instead of failing — so the feature behind them looks
                        empty rather than broken. Usually it means a table shipped
                        without being registered in the row-security script; applying
                        the current version fixes it once the table is registered.
                        {strandedRows.map((r) => (
                            <div key={r.key} className="mt-1">
                                <span className="font-medium">{r.label}</span>
                                <span className="font-mono ml-1 break-words">
                                    {r.unpolicedTables.join(", ")}
                                </span>
                            </div>
                        ))}
                    </Callout>
                )}
                {!loading &&
                    coverageChecked &&
                    strandedRows.length === 0 &&
                    !loadError && (
                        <Callout tone="neutral" icon={ShieldCheck} size="xs">
                            Every table with row security on has at least one policy —
                            nothing is locked to nobody.
                        </Callout>
                    )}

                {/* Staged RLS rollout — only when a canary tenant is configured */}
                {canaryTenantSlug && (
                    <Card className="p-3 border-border-primary">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                            <div className="min-w-0">
                                <div className="text-[12px] font-semibold text-text-primary flex items-center gap-1.5">
                                    <ShieldCheck
                                        size={13}
                                        aria-hidden="true"
                                        className="text-tone-violet"
                                    />
                                    Staged RLS rollout
                                </div>
                                <div className="text-[11px] text-text-secondary mt-0.5">
                                    {canaryTenantId ? (
                                        <>
                                            The canary is{" "}
                                            <span className="font-medium text-text-primary">
                                                {canaryTenantSlug}
                                            </span>
                                            . Apply there, verify it, then roll out to
                                            the rest.
                                        </>
                                    ) : (
                                        <span className="text-tone-rose">
                                            PLATFORM_RLS_CANARY_TENANT_SLUG names{" "}
                                            <span className="font-mono">
                                                {canaryTenantSlug}
                                            </span>
                                            , and no dedicated tenant carries that slug.
                                            Fix the setting, or apply per database from
                                            the rows below.
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-wrap items-stretch gap-2 md:ml-auto">
                                <RolloutStep
                                    step={1}
                                    done={canaryCurrent}
                                    state={
                                        !canaryReachable
                                            ? "The canary could not be read."
                                            : canaryCurrent
                                            ? `Canary is on v${canaryRow.rlsCurrent}.`
                                            : `Canary is on v${
                                                  canaryRow.rlsCurrent ?? "—"
                                              }, code is on v${rlsCodeVersion}.`
                                    }
                                >
                                    <Button
                                        onClick={onApplyCanary}
                                        disabled={controlsBusy || !canaryBehind}
                                        title={
                                            !canaryReachable
                                                ? "The canary database could not be read"
                                                : canaryCurrent
                                                ? "The canary already carries the current version"
                                                : `Apply RLS v${rlsCodeVersion} to ${canaryTenantSlug}`
                                        }
                                        className={PILL_PRIMARY}
                                    >
                                        <Play size={12} aria-hidden="true" />
                                        {canaryInFlight
                                            ? "Applying…"
                                            : `Apply to ${canaryTenantSlug}`}
                                    </Button>
                                </RolloutStep>
                                <RolloutStep
                                    step={2}
                                    done={canaryCurrent && remainingBehind.length === 0}
                                    state={
                                        !canaryCurrent
                                            ? "Waiting on step 1."
                                            : remainingBehind.length === 0
                                            ? "Every other database is current."
                                            : `${plural(
                                                  remainingBehind.length,
                                                  "database is",
                                                  "databases are"
                                              )} behind.`
                                    }
                                >
                                    <Button
                                        onClick={onApplyRemaining}
                                        disabled={
                                            controlsBusy ||
                                            !canaryCurrent ||
                                            remainingBehind.length === 0
                                        }
                                        title={
                                            !canaryCurrent
                                                ? "Apply to the canary first"
                                                : remainingBehind.length === 0
                                                ? "No other database is behind"
                                                : "Apply RLS to every database except the canary, the platform DB included"
                                        }
                                        className={`${PILL} ${SOLID_WARNING}`}
                                    >
                                        <Play size={12} aria-hidden="true" />
                                        {remainingInFlight
                                            ? "Rolling out…"
                                            : "Apply to the rest"}
                                    </Button>
                                </RolloutStep>
                            </div>
                        </div>
                    </Card>
                )}

                {/* Search — client-side filter over the fleet snapshot */}
                <div className="flex items-center gap-2.5">
                    <div className="flex-1">
                        <SearchInput
                            value={search}
                            onChange={setSearch}
                            placeholder="Search by label, host, database name or slug"
                        />
                    </div>
                    {!loading && search.trim() !== "" && (
                        <div className="text-[11px] text-text-secondary whitespace-nowrap">
                            {filteredRows.length} of {rows.length} shown
                        </div>
                    )}
                </div>

                {/* Fleet table */}
                <Card className="p-0 bg-table-heading border-border-primary overflow-hidden">
                    {loading ? (
                        <TableSkeleton rows={5} />
                    ) : loadError ? (
                        <EmptyState
                            icon={AlertTriangle}
                            title="The fleet snapshot could not be loaded"
                            body={loadError}
                            action={
                                <Button
                                    onClick={() => load()}
                                    className={PILL_PRIMARY}
                                >
                                    <RefreshCw size={12} aria-hidden="true" />
                                    Try again
                                </Button>
                            }
                        />
                    ) : rows.length === 0 ? (
                        <EmptyState
                            icon={Database}
                            title="No databases to show"
                            body="The platform database and every dedicated tenant database appear here. Refresh once a tenant has been provisioned."
                        />
                    ) : filteredRows.length === 0 ? (
                        <EmptyState
                            icon={Database}
                            title={`Nothing matches “${search.trim()}”`}
                            body="Search runs over the label, host, database name and tenant slug."
                            action={
                                <Button
                                    onClick={() => setSearch("")}
                                    className={PILL_NEUTRAL}
                                >
                                    Clear the search
                                </Button>
                            }
                        />
                    ) : (
                        <DataTable
                            columns={COLUMNS}
                            apiData={tableRows}
                            rowKey="key"
                            sortable
                            defaultSort={{ key: "label", order: "asc" }}
                            paginationLabel="databases"
                            className="rounded-none"
                            currentPage={page}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                        />
                    )}
                </Card>
            </div>

            {applyTarget && (
                <RlsApplyModal
                    row={applyTarget}
                    previewOnly={previewMode}
                    rlsCodeVersion={rlsCodeVersion}
                    onClose={() => setApplyTarget(null)}
                    onCompleted={() => {
                        setApplyTarget(null);
                        load();
                    }}
                />
            )}

            {permApplyTarget && (
                <PermissionsSyncModal
                    row={permApplyTarget}
                    onClose={() => setPermApplyTarget(null)}
                    onCompleted={() => {
                        setPermApplyTarget(null);
                        load();
                    }}
                />
            )}

            {alembicTarget && (
                <AlembicApplyModal
                    row={alembicTarget}
                    alembicHead={alembicHead}
                    catalog={catalog}
                    onClose={() => setAlembicTarget(null)}
                    onRefresh={() => load()}
                    onCompleted={() => {
                        setAlembicTarget(null);
                        load();
                    }}
                />
            )}

            {historyRow && (
                <MigrationHistoryModal
                    row={historyRow}
                    onClose={() => setHistoryRow(null)}
                    onRetryDone={() => load()}
                />
            )}

            {rlsDetailRow && (
                <RlsVersionDrawer
                    row={rlsDetailRow}
                    catalog={catalog}
                    loading={catalog.loading}
                    onClose={() => setRlsDetailRow(null)}
                />
            )}

            {schemaDetailRow && (
                <SchemaRevisionDrawer
                    row={schemaDetailRow}
                    catalog={catalog}
                    loading={catalog.loading}
                    onClose={() => setSchemaDetailRow(null)}
                />
            )}

            {permDetailRow && (
                <PermissionsDetailDrawer
                    row={permDetailRow}
                    onClose={() => setPermDetailRow(null)}
                />
            )}

            <RlsFleetRollbackModal
                open={fleetRollbackOpen}
                rlsCurrent={rlsCodeVersion}
                catalog={catalog}
                onClose={() => setFleetRollbackOpen(false)}
                onRefresh={load}
                onCompleted={() => {
                    setFleetRollbackOpen(false);
                    load();
                }}
            />

            <AlertModal
                open={reconcileConfirmOpen}
                onOpenChange={(o) =>
                    !o && !reconcileInFlight && setReconcileConfirmOpen(false)
                }
                title="Reconcile the fleet?"
                description={`This runs alembic upgrade head and then applies RLS on every dedicated tenant database — ${plural(
                    alembicBehind.length,
                    "database",
                    "databases"
                )} behind on schema, ${plural(
                    rlsBehind.filter((r) => !r.isPlatform).length,
                    "database",
                    "databases"
                )} behind on row security. A database that fails does not stop the rest. The platform database is not touched.`}
                cancelText="Cancel"
                actionText="Reconcile"
                onCancel={() => setReconcileConfirmOpen(false)}
                onAction={onReconcileConfirm}
                loading={reconcileInFlight}
                actionClassName="bg-button-secondary hover:bg-button-secondary/85 text-text-base"
            />

            <AlertModal
                open={!!rlsRollbackTarget}
                onOpenChange={(o) =>
                    !o && !rlsRollbackInFlight && setRlsRollbackTarget(null)
                }
                title="Roll back row security by one version?"
                description={rlsRollbackDescription(rlsRollbackTarget, catalog)}
                cancelText="Cancel"
                actionText="Roll back"
                onCancel={() => setRlsRollbackTarget(null)}
                onAction={onRlsRollbackConfirm}
                loading={rlsRollbackInFlight}
                actionClassName={SOLID_WARNING}
            />

            {/* A dedicated modal rather than a plain confirm, because the SQL a
                downgrade runs is knowable and this is the one destructive action
                on the screen where nobody could see it. Same interaction as the
                dialog it replaces: cancel, or confirm. */}
            <AlembicDowngradeModal
                target={downgradeTarget}
                catalog={catalog}
                dedicatedCount={dedicatedRows.length}
                downgrading={downgradeInFlight}
                onCancel={() => setDowngradeTarget(null)}
                onConfirm={onDowngradeConfirm}
            />
        </PermissionGuard>
    );
};

// ── The per-database rollback confirmation ──────────────────────────────────
//
// One version back is one version's worth of policies gone, and which policies
// those are decides whether this is routine or an outage. The target version's
// name and its deny-all list both come from the catalogue, so this reads the
// same as the drawer and the fleet-wide modal.
//
// Phrasing content only — Radix renders the description inside a `<p>`.
const rlsRollbackDescription = (target, catalog) => {
    if (!target?.rlsCurrent) return "";

    const version = target.rlsCurrent - 1;
    const entry = catalog?.rlsVersion(version);
    const name = entry?.title ? ` (${entry.title})` : "";
    const where = target.tenantSlug || "this database";

    return (
        <>
            <span className="block">
                This applies archived RLS v{version}
                {name} to {where}, replacing the v{target.rlsCurrent} policies it
                runs on now. The rest of the fleet is untouched.
            </span>
            {!entry && (
                <span className="block mt-2 text-tone-amber">
                    v{version} is not in this build's archive, so what it does cannot
                    be shown here — and the apply will be refused.
                </span>
            )}
            <RollbackHazard entry={entry} inline />
        </>
    );
};

// ── Chip tooltips ───────────────────────────────────────────────────────────
//
// The chip itself can only hold a hash or a version number. These say what that
// value *is* and where it *stands* — two different facts, so two lines rather
// than one run-on sentence. `StateChip` routes them through `CustomTooltip`,
// which renders a node and wraps at `max-w-xs`.
//
// Both fall back to the bare value when the catalogue could not be loaded, so a
// server that sends no descriptions still reads correctly.

// One identity line, one status line. Kept here so the two columns cannot drift
// into describing themselves differently.
const chipTooltip = (identity, status) => (
    <>
        <span className="block font-semibold">{identity}</span>
        <span className="block text-text-secondary font-normal">{status}</span>
    </>
);

const schemaTitle = (row, catalog) => {
    if (row.error) return "The database could not be read.";
    if (!row.alembicCurrent) {
        return "No schema revision is recorded on this database. Open for detail.";
    }

    const described = catalog?.revision(row.alembicCurrent);
    const identity = described?.title
        ? `${row.alembicCurrent} — ${described.title}`
        : row.alembicCurrent;

    if (row.alembicPosition === "diverged") {
        return chipTooltip(
            identity,
            "This build has no such revision, so upgrading will not fix it. Open for detail."
        );
    }
    if (row.alembicPosition === "behind" && row.pendingRevisionCount) {
        return chipTooltip(
            identity,
            `${plural(
                row.pendingRevisionCount,
                "revision",
                "revisions"
            )} behind the code head. Open to see which.`
        );
    }
    if (row.alembicDrifted) {
        return chipTooltip(
            identity,
            `The code head is ${row.alembicHead}. Open for detail.`
        );
    }
    return chipTooltip(identity, "Matches the code head. Open for detail.");
};

const rlsTitle = (row, catalog) => {
    if (row.error) return "The database could not be read.";
    if (row.rlsCurrent == null) {
        return "No row-security version is recorded on this database. Open for detail.";
    }

    const entry = catalog?.rlsVersion(row.rlsCurrent);
    const identity = entry?.title
        ? `v${row.rlsCurrent} — ${entry.title}`
        : `v${row.rlsCurrent}`;

    if (row.rlsPosition === "ahead") {
        return chipTooltip(
            identity,
            `Ahead of the code, which is on v${row.rlsCodeVersion}. Open for detail.`
        );
    }
    if (row.rlsDrifted) {
        return chipTooltip(
            identity,
            `The code is on v${row.rlsCodeVersion}. Open for detail.`
        );
    }
    return chipTooltip(identity, "Matches the code. Open for detail.");
};

export default DatabaseManagement;
